import React from 'react';
import { ControllerProps, Path, PathValue, useController, useFormContext } from 'react-hook-form';
import { ActionMeta } from 'react-select';
import { OptionsType, OptionTypeBase } from 'react-select/src/types';
import FormHelperText from '@material-ui/core/FormHelperText/FormHelperText';

import { SelectInput, SelectInputProps, SelectOption } from 'features/app/components/SelectInput';

export type SelectInputFieldProps<T> = Omit<ControllerProps<T>, 'as' | 'render' | 'name' | 'defaultValue'> &
  SelectInputProps & {
    helperText?: string;
    maxLength?: number;
    helperTextClass?: string;
  };

export const SelectInputField = <T,>({
  name,
  defaultValue,
  rules,
  control,
  helperText,
  onChange: onChangeSelectField = () => ({}),
  onBlur: onBlurSelectField = () => ({}),
  maxLength,
  ...props
}: SelectInputFieldProps<T>) => {
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name: name as Path<T>,
    defaultValue: defaultValue as PathValue<T, Path<T>>,
    rules,
    control,
  });
  const { setError } = useFormContext() ?? {};

  const { isMulti } = props;

  const handleChange = (
    newValue: OptionTypeBase | OptionsType<OptionTypeBase> | null,
    action: ActionMeta<OptionTypeBase>
  ) => {
    let resultValue = newValue;
    if (isMulti && maxLength && newValue?.length > maxLength) {
      resultValue = newValue?.filter(
        (item: SelectOption) => !(value as SelectOption[]).find((i: SelectOption) => i.value === item.value)
      );
    }

    if (error?.message && typeof setError === 'function') setError(name, { message: '' });
    onChange(resultValue);
    onChangeSelectField(resultValue, action);
  };

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    onBlur();
    onBlurSelectField(e);
  };

  return (
    <>
      <SelectInput
        {...props}
        ref={ref}
        value={value as SelectOption | SelectOption[]}
        onChange={handleChange}
        onBlur={handleBlur}
        name={name}
        defaultValue={defaultValue}
      />
      {error && (
        <FormHelperText error={true} className={props?.helperTextClass}>
          {helperText || error?.message}
        </FormHelperText>
      )}
    </>
  );
};
